import { useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { PiPaperclip } from 'react-icons/pi'
import { EmailThread } from 'models/emailIntegration'

/** Format a date string as time-only (today), "Mon DD" (this year), or "MM/DD/YY" (older). */
function formatThreadDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const isThisYear = date.getFullYear() === now.getFullYear()

  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  if (isThisYear) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
  }

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  })
}

/** TanStack Table column definitions for the email thread inbox list. */
export function useInboxThreadColumns(): ColumnDef<EmailThread, any>[] {
  return useMemo(
    () => [
      {
        id: 'sender',
        accessorKey: 'latest_sender',
        header: '',
        size: 160,
        minSize: 160,
        maxSize: 160,
        cell: ({ row }) => {
          const sender = row.original.latest_sender
          const name =
            sender?.[0]?.name || sender?.[0]?.email || 'Unknown Sender'
          return (
            <span className="font-bold text-sm text-[#202124] truncate block">
              {name}
            </span>
          )
        },
      },
      {
        id: 'subject_preview',
        accessorKey: 'subject',
        header: '',
        cell: ({ row }) => {
          const { subject, latest_snippet, has_attachments, message_count } =
            row.original
          return (
            <div className="flex items-center gap-1.5 min-w-0">
              {message_count > 1 && (
                <span className="text-xs text-[#6E6E6E] flex-shrink-0">
                  {message_count}
                </span>
              )}
              <span className="truncate min-w-0">
                <span className="font-bold text-sm text-[#202124]">
                  {subject || '(no subject)'}
                </span>
                {latest_snippet && (
                  <span className="text-sm text-black/[0.54]">
                    {' '}
                    - {latest_snippet}
                  </span>
                )}
              </span>
              {has_attachments && (
                <PiPaperclip className="size-[22px] text-[#6E6E6E] flex-shrink-0" />
              )}
            </div>
          )
        },
      },
      {
        id: 'timestamp',
        accessorKey: 'latest_message_date',
        header: '',
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: ({ row }) => {
          const date = row.original.latest_message_date
          if (!date) return null
          return (
            <span className="text-sm font-bold text-[#202124] whitespace-nowrap text-right block">
              {formatThreadDate(date)}
            </span>
          )
        },
      },
    ],
    []
  )
}
